import { MaterialHeader } from '../../layout/components/MaterialHeader.tsx';
import { MaterialTitle } from '../../layout/components/MaterialTitle.tsx';
import {
    AccessTime,
    ArrowCircleDown,
    CheckCircleOutlined,
    CloudDownload,
} from '@mui/icons-material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { MainContent } from '../../shared-mui-components/MainContent.tsx';
import { EmptyResults } from '../../shared-mui-components/ListMessages.tsx';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { SeparationBar } from '../../layout/components/SeparationBar.tsx';
import {
    ReadOnlyDownloadState,
    ReadOnlyExportDownlaodItem,
    useReadOnlyExportContext,
} from '../../app/components/ReadOnlyExportContext.tsx';
import { urlWithOperatorExternalId } from '../../utils/Utils.ts';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { envIsProduction } from 'dg-web-shared/lib/Environment.ts';

export function DownloadsOverview() {
    const { refetchReadOnlyExportData, downloadList } =
        useReadOnlyExportContext();

    return (
        <MainContent>
            <MaterialHeader editMode={false}>
                <MaterialTitle
                    Icon={CloudDownload}
                    title={
                        <Localized
                            de="Downloads"
                            fr="Téléchargements"
                            it="Downloads"
                            en="Downloads"
                        />
                    }
                />
            </MaterialHeader>
            <SeparationBar />
            {downloadList.length === 0 ? (
                <EmptyResults hasFilter={false} />
            ) : (
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Localized
                                        de="Status"
                                        fr="État"
                                        it="Stato"
                                        en="State"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Localized
                                        de="Typ"
                                        fr="Type"
                                        it="Tipo"
                                        en="Type"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Localized
                                        de="Angefordert am"
                                        fr="Demandé le"
                                        it="Richiesto il"
                                        en="Requested on"
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {downloadList.map(downlaodItem => (
                                <DownloadRow
                                    key={downlaodItem.readOnlyExportId}
                                    downloadItem={downlaodItem}
                                    onDownload={() => {
                                        refetchReadOnlyExportData();
                                    }}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </MainContent>
    );
}

function DownloadRow({
    downloadItem,
    onDownload,
}: {
    downloadItem: ReadOnlyExportDownlaodItem;
    onDownload: () => void;
}) {
    const { operatorExternalId } = useOperatorContext();
    const downloadable =
        downloadItem.state !== ReadOnlyDownloadState.BEING_CREATED;
    return (
        <TableRow
            hover={downloadable}
            sx={{ cursor: downloadable ? 'pointer' : undefined }}
            onClick={
                downloadable
                    ? () => {
                          openNewTab(
                              urlWithOperatorExternalId(
                                  `/ui-api/operator-account/export/${downloadItem.readOnlyExportId}`,
                                  operatorExternalId,
                              ),
                          );
                          onDownload();
                      }
                    : undefined
            }
        >
            <TableCell>
                <DownloadStatusIcon state={downloadItem.state} />
            </TableCell>
            <TableCell>
                <Localized {...downloadItem.typeName} />
                {!envIsProduction() &&
                    downloadItem.state ==
                        ReadOnlyDownloadState.BEING_CREATED && (
                        <div>
                            Nur nicht PROD: {downloadItem.readOnlyExportId}
                        </div>
                    )}
            </TableCell>
            <TableCell>
                {Formatter.dayMonthYearHourMinute(
                    DateTime.fromISO(downloadItem.createdAt),
                )}
            </TableCell>
        </TableRow>
    );
}

function DownloadStatusIcon({ state }: { state: ReadOnlyDownloadState }) {
    switch (state) {
        case ReadOnlyDownloadState.BEING_CREATED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="Wird erstellt..."
                            fr="En cours de création..."
                            it="In fase di creazione..."
                            en="Being created..."
                        />
                    }
                    placement="right"
                >
                    <AccessTime color="warning" />
                </Tooltip>
            );
        case ReadOnlyDownloadState.READY:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="Bereit"
                            fr="Prêt"
                            it="Pronto"
                            en="Ready"
                        />
                    }
                    placement="right"
                >
                    <ArrowCircleDown color="warning" />
                </Tooltip>
            );
        case ReadOnlyDownloadState.DOWNLOADED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="Heruntergeladen"
                            fr="Téléchargé"
                            it="Scaricato"
                            en="Downloaded"
                        />
                    }
                    placement="right"
                >
                    <CheckCircleOutlined color="disabled" />
                </Tooltip>
            );
    }
}
